import { Delete, Save, SaveAs } from '@mui/icons-material';
import {
    Box,
    Button,
    Divider,
    FormControl,
    FormHelperText,
    IconButton,
    InputLabel,
    ListItemText,
    MenuItem,
    Select,
    SelectChangeEvent,
    styled,
} from '@mui/material';
import { GridApi } from 'ag-grid-community';
import { Dispatch, SetStateAction, useCallback, useEffect, useState } from 'react';
import SaveSettingsDialog from 'src/components/molecules/SaveSettingsDialog/SaveSettingsDialog';
import { Module } from 'src/domain';
import { useChangesSnackbar } from 'src/hooks/snackbar/useChangesSnackbar';
import { useDialog } from 'src/hooks/useDialog';
import { useSaveUserSettings } from 'src/hooks/useSaveUserSettings';
import { MergedUserSettingModel, useUserSettings } from 'src/hooks/useUserSettings';
import { DeleteDialog } from '../DeleteDialog/DeleteDialog';

interface SettingsDropdownProps {
    gridApi: GridApi | null;
    module?: Module;
    setEnableAdvancedFiltering?: Dispatch<SetStateAction<boolean>>;
}
const SettingsDropdown = ({ gridApi, module, setEnableAdvancedFiltering }: SettingsDropdownProps) => {
    const { mergedSettings } = useUserSettings(module);
    const [selectedSettingName, setSelectedSettingName] = useState<string>('');
    const [settingToDelete, setSettingToDelete] = useState<MergedUserSettingModel | null>(null);
    const { saveUserSettings, updateUserSettings, deleteUserSettings } = useSaveUserSettings(module);
    const [isSaving, setIsSaving] = useState(false);
    const showChangesSnackbar = useChangesSnackbar();
    const { isOpen: isSaveDialogOpen, openDialog: openSaveDialog, closeDialog: closeSaveDialog } = useDialog();
    const { isOpen: isDeleteDialogOpen, openDialog: openDeleteDialog, closeDialog: closeDeleteDialog } = useDialog();

    const applySetting = useCallback(
        (selectedName: string) => {
            const selectedSetting = mergedSettings.find((setting) => setting.name === selectedName);
            if (gridApi && selectedSetting) {
                if (selectedSetting.filterSettings) {
                    const filterModel = JSON.parse(selectedSetting.filterSettings);
                    // Advanced filter models have 'filterType' at the top level, that could be 'join' for multiple conditions,
                    // or if it's a filter only on one column it's the type of that column filter.
                    // Regular filter model is an object with column names as properties instead.
                    if (filterModel?.filterType) {
                        setEnableAdvancedFiltering?.(true);
                        gridApi.setGridOption('enableAdvancedFilter', true);
                        gridApi.setAdvancedFilterModel(filterModel);
                    } else {
                        setEnableAdvancedFiltering?.(false);
                        gridApi.setGridOption('enableAdvancedFilter', false);
                        gridApi.setFilterModel(filterModel);
                    }
                }
                if (selectedSetting.columnSettings) {
                    const columnState = JSON.parse(selectedSetting.columnSettings);
                    gridApi.applyColumnState({ state: columnState, applyOrder: true });
                }
            }
        },
        [gridApi, mergedSettings, setEnableAdvancedFiltering]
    );

    useEffect(() => {
        if (selectedSettingName) {
            applySetting(selectedSettingName);
        }
    }, [applySetting, selectedSettingName]);

    const handleSelectSetting = (e: SelectChangeEvent) => {
        const selectedName = e.target.value;
        if (!selectedName) return;

        setSelectedSettingName(selectedName);
    };

    const completeChanges = async (requests: Promise<unknown>[], onSuccess: () => void) => {
        if (!requests.length) return;
        setIsSaving(true);
        try {
            const results = await Promise.allSettled(requests);
            const failure = results.find((result) => result.status === 'rejected');
            if (failure) throw failure.reason;
            onSuccess();
            showChangesSnackbar(undefined, { showCubeTimer: false });
            return true;
        } catch (error: unknown) {
            showChangesSnackbar(error);
            return false;
        } finally {
            setIsSaving(false);
        }
    };

    const handleSave = async () => {
        if (gridApi) {
            if (selectedSettingName) {
                const selectedSetting = mergedSettings.find((setting) => setting.name === selectedSettingName);
                if (!selectedSetting) return;

                const currentFilterModel = gridApi.getFilterModel();
                const advancedFilterModel = gridApi.getAdvancedFilterModel();
                const currentColumnState = gridApi.getColumnState();

                const updatePromises = [];
                if (selectedSetting.filterSettingsId && selectedSetting.filterSettings) {
                    advancedFilterModel
                        ? updatePromises.push(
                              updateUserSettings(
                                  selectedSetting.filterSettingsId,
                                  selectedSettingName,
                                  JSON.stringify(advancedFilterModel)
                              )
                          )
                        : updatePromises.push(
                              updateUserSettings(
                                  selectedSetting.filterSettingsId,
                                  selectedSettingName,
                                  JSON.stringify(currentFilterModel)
                              )
                          );
                } else {
                    currentFilterModel
                        ? updatePromises.push(
                              saveUserSettings(selectedSettingName, JSON.stringify(currentFilterModel), 'Filters')
                          )
                        : updatePromises.push(
                              saveUserSettings(selectedSettingName, JSON.stringify(advancedFilterModel), 'Filters')
                          );
                }

                if (selectedSetting.columnSettingsId) {
                    updatePromises.push(
                        updateUserSettings(
                            selectedSetting.columnSettingsId,
                            selectedSettingName,
                            JSON.stringify(currentColumnState)
                        )
                    );
                } else {
                    updatePromises.push(
                        saveUserSettings(selectedSettingName, JSON.stringify(currentColumnState), 'Columns')
                    );
                }

                return completeChanges(updatePromises, closeSaveDialog);
            } else {
                openSaveDialog();
            }
        }
    };

    const handleSaveAs = () => {
        if (gridApi) {
            openSaveDialog();
        }
    };

    const handleSaveSetting = async (filterName: string) => {
        if (!gridApi) return;

        const promises = [];

        const currentFilterModel = gridApi.getFilterModel();
        const advancedFilterModel = gridApi.getAdvancedFilterModel();
        const currentColumnState = gridApi.getColumnState();

        if (currentFilterModel && Object.keys(currentFilterModel).length > 0) {
            promises.push(saveUserSettings(filterName, JSON.stringify(currentFilterModel), 'Filters'));
        }
        if (advancedFilterModel) {
            promises.push(saveUserSettings(filterName, JSON.stringify(advancedFilterModel), 'Filters'));
        }

        if (currentColumnState) {
            promises.push(saveUserSettings(filterName, JSON.stringify(currentColumnState), 'Columns'));
        }

        return completeChanges(promises, () => {
            setSelectedSettingName(filterName);
            closeSaveDialog();
        });
    };

    const clearSettings = () => {
        setSelectedSettingName('');
        if (gridApi) {
            gridApi.setFilterModel(null);
            gridApi.setAdvancedFilterModel(null);
            gridApi.resetColumnState();
        }
    };

    const handleDeleteButtonClick = (setting: MergedUserSettingModel, event: React.MouseEvent) => {
        event.stopPropagation();
        setSettingToDelete(setting);
        openDeleteDialog();
    };

    const handleDeleteSetting = async () => {
        if (!settingToDelete) return;

        closeDeleteDialog();
        const deletePromises = [];

        if (settingToDelete.filterSettingsId) {
            deletePromises.push(deleteUserSettings(settingToDelete.filterSettingsId));
        }

        if (settingToDelete.columnSettingsId) {
            deletePromises.push(deleteUserSettings(settingToDelete.columnSettingsId));
        }

        await completeChanges(deletePromises, clearSettings);
    };
    return (
        <>
            <Box
                className="filter-and-column-settings"
                sx={{
                    display: 'flex',
                    gap: 2,
                }}
            >
                <FormControl sx={{ m: 1, minWidth: 120 }} size="small">
                    <InputLabel>Filter and column settings</InputLabel>
                    <Select
                        size="small"
                        sx={{
                            width: '250px',
                            '& .MuiSelect-select': {
                                height: '1.4375em',
                                '& .MuiListItemText-root': {
                                    marginTop: 0,
                                },
                            },
                        }}
                        value={selectedSettingName}
                        onChange={handleSelectSetting}
                        label="Filter and column settings"
                    >
                        {mergedSettings.map((setting) => (
                            <CustomMenuItem key={setting.name} value={setting.name}>
                                <ListItemText>{setting.name}</ListItemText>

                                <CustomDeleteButton
                                    className="delete-icon"
                                    edge="end"
                                    onClick={(event) => {
                                        handleDeleteButtonClick(setting, event);
                                    }}
                                >
                                    <Delete fontSize="small" />
                                </CustomDeleteButton>
                            </CustomMenuItem>
                        ))}

                        <Divider />
                        <MenuItem sx={{ paddingY: 0 }} onClick={handleSave} disabled={isSaving}>
                            <Button variant="text" startIcon={<Save />}>
                                Save
                            </Button>
                        </MenuItem>
                        <MenuItem sx={{ paddingY: 0 }} onClick={handleSaveAs}>
                            <Button variant="text" startIcon={<SaveAs />}>
                                Save as...
                            </Button>
                        </MenuItem>
                    </Select>
                    {selectedSettingName && (
                        <FormHelperText
                            sx={{
                                display: 'flex',
                                justifyContent: 'flex-end',
                                margin: '0px 4px',
                            }}
                        >
                            <Button
                                variant="text"
                                sx={{ padding: 0, textDecoration: 'underline' }}
                                onClick={clearSettings}
                            >
                                Clear settings
                            </Button>
                        </FormHelperText>
                    )}
                </FormControl>
            </Box>
            <SaveSettingsDialog
                open={isSaveDialogOpen}
                onClose={closeSaveDialog}
                onSave={handleSaveSetting}
                isSaving={isSaving}
            />
            <DeleteDialog
                open={isDeleteDialogOpen}
                title="Delete setting"
                content={`Are you sure you want to delete setting "${settingToDelete?.name}"?  Deleting is permanent and cannot be undone.`}
                onCancel={closeDeleteDialog}
                onDelete={handleDeleteSetting}
            />
        </>
    );
};
export default SettingsDropdown;

const CustomMenuItem = styled(MenuItem)(() => ({
    maxWidth: '250px',
    minHeight: '40px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    '&:hover .delete-icon': {
        visibility: 'visible',
        display: 'inline-flex',
    },
    '.delete-icon': {
        visibility: 'hidden',
        display: 'none',
    },
}));

const CustomDeleteButton = styled(IconButton)(() => ({
    visibility: 'hidden',
    display: 'none',
    padding: '2px',
}));
